import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAffixModule } from 'ng-zorro-antd/affix';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzResizeEvent, NzResizableModule } from 'ng-zorro-antd/resizable';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

import { GcpLogListComponent } from '../gcp/gcp-log-list.component';
import { LogListFilterPopupComponent } from './log-list-filter-popup/log-list-filter-popup.component';

@Component({
  selector: 'app-log-list',
  standalone: true,
  imports: [
    CommonModule,
    NzTableModule,
    NzTabsModule,
    NzSpinModule,
    NzBreadCrumbModule,
    NzPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    NzFormModule,
    NzSelectModule,
    NzModalModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    NzToolTipModule,
    NzEmptyModule,
    NzTagModule,
    NzAffixModule,
    NzCheckboxModule,
    NzDropDownModule,
    NzMenuModule,
    NzResizableModule,
    I18nPipe,
    TestIdDirective,
    GcpLogListComponent
  ],
  templateUrl: './log-list.component.html',
  styleUrls: ['./log-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LogListComponent implements OnInit {
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly http = inject(HttpService);
  private readonly router = inject(Router);
  private readonly location = inject(Location);

  private readonly nzModalService = inject(NzModalService);
  loading = false;
  selectedLogTabIndex = 0;
  listOfData: any[] = [];
  allColumns = [
    { title: this.i18n.fanyi('log.account-name'), key: 'name' },
    { title: this.i18n.fanyi('log.trace-id'), key: 'traceId' },
    { title: this.i18n.fanyi('log.client-trace-id'), key: 'clientTraceId' },
    { title: this.i18n.fanyi('log.request-path'), key: 'requestPath' },
    { title: this.i18n.fanyi('log.request-time'), key: 'requestTime' },
    { title: this.i18n.fanyi('log.device-id'), key: 'uniqueId' },
    { title: this.i18n.fanyi('log.status'), key: 'hasError' },
    { title: this.i18n.fanyi('log.time-cost'), key: 'timeCost' },
    { title: this.i18n.fanyi('log.os-type'), key: 'osType' },
    { title: this.i18n.fanyi('log.device-name'), key: 'deviceName' },
    { title: this.i18n.fanyi('log.channel-source'), key: 'channel' }
  ];
  columns = [...this.allColumns];
  selectedColumnKeys: string[] = this.allColumns.map(c => c.key);

  allFilterFields = [
    { key: 'tokenId', label: this.i18n.fanyi('log.token-id') },
    { key: 'clientTraceId', label: this.i18n.fanyi('log.client-trace-id') },
    { key: 'requestPath', label: this.i18n.fanyi('log.request-path') },
    { key: 'channel', label: this.i18n.fanyi('log.channel-source') },
    { key: 'osType', label: this.i18n.fanyi('log.os-type') },
    { key: 'requestTimeRange', label: this.i18n.fanyi('log.request-time') },
    { key: 'hasError', label: this.i18n.fanyi('log.status') },
    { key: 'timeCost', label: this.i18n.fanyi('log.time-cost') },
    { key: 'uniqueId', label: this.i18n.fanyi('log.device-id') },
    { key: 'name', label: this.i18n.fanyi('log.account-name') },
    { key: 'deviceName', label: this.i18n.fanyi('log.device-name') },
    { key: 'traceId', label: this.i18n.fanyi('log.trace-id') }
  ];
  selectedFilterKeys: string[] = ['clientTraceId', 'requestTimeRange'];

  filterParamsKey = [
    { key: 'name', label: this.i18n.fanyi('log.account-name'), value: '' },
    { key: 'traceId', label: this.i18n.fanyi('log.trace-id'), value: '' },
    { key: 'clientTraceId', label: this.i18n.fanyi('log.client-trace-id'), value: '' },
    { key: 'requestPath', label: this.i18n.fanyi('log.request-path'), value: '' },
    { key: 'channel', label: this.i18n.fanyi('log.channel-source'), value: '' },
    { key: 'requestTime', label: this.i18n.fanyi('log.request-time'), value: '' },
    { key: 'tokenId', label: this.i18n.fanyi('log.token-id'), value: '' },
    { key: 'osType', label: this.i18n.fanyi('log.os-type'), value: '' },
    { key: 'deviceName', label: this.i18n.fanyi('log.device-name'), value: '' },
    { key: 'uniqueId', label: this.i18n.fanyi('log.device-id'), value: '' },
    { key: 'hasError', label: this.i18n.fanyi('log.status'), value: '' },
    { key: 'timeCost', label: this.i18n.fanyi('log.time-cost'), value: '' }
  ];
  pageIndex = 1;
  pageSize = 20;
  total = 0;
  direction = 'desc';
  refreshTime = Date.now();

  /**
   * Keep the paginator visible when an older API response omits totalCount.
   * The current page size is the safest fallback until the backend returns a
   * complete total.
   */
  get paginationTotal(): number {
    return Math.max(Number(this.total) || 0, this.listOfData.length);
  }

  osTypeOptions = ['web', 'mac', 'android', 'ios', 'pc'];
  channelOptions = [
    'Aile',
    'Aiwow',
    'AileLite',
    'Webchat',
    'Line',
    'Facebook',
    'Instagram',
    'Google',
    'Cbm',
    'Ai',
    'Admin',
    'Anonymous',
    'Internal',
    'LiteConsole'
  ];
  statusOptions = [
    { label: this.i18n.fanyi('log.success'), value: 'false' },
    { label: this.i18n.fanyi('log.failed'), value: 'true' }
  ];
  filterParams: any = {
    keywords: '',
    name: '',
    traceId: '',
    clientTraceId: '',
    requestPath: '',
    channel: '',
    tokenId: '',
    osType: '',
    deviceName: '',
    requestTimeRange: [],
    hasError: '',
    timeCost: '',
    uniqueId: ''
  };

  constructor() {}

  // 存储列宽的对象
  columnWidths: { [key: string]: string } = {
    requestTime: '220px', // 增加请求时间列宽
    requestPath: '300px', // 增加请求路径列宽
    uniqueId: '220px', // 增加设备唯一值列宽
    traceId: '180px',
    clientTraceId: '180px',
    name: '100px',
    accountId: '150px',
    timeCost: '100px',
    hasError: '100px',
    channel: '120px'
  };

  // 获取列宽度
  getColumnWidth(key: string): string {
    return this.columnWidths[key] ?? '150px';
  }

  get tableScrollX(): string {
    const rowNumberWidth = 60;
    const visibleColumnsWidth = this.columns.reduce((total, column) => {
      const width = Number.parseFloat(this.getColumnWidth(column.key));
      return total + (Number.isFinite(width) ? width : 150);
    }, 0);
    return `${rowNumberWidth + visibleColumnsWidth}px`;
  }

  syncHorizontalScroll(event: Event): void {
    const source = event.target as HTMLElement;
    const scrollbar = source.closest('.page-container')?.querySelector('.table-horizontal-scrollbar') as HTMLElement | null;
    if (scrollbar && Math.abs(scrollbar.scrollLeft - source.scrollLeft) >= 1) {
      scrollbar.scrollLeft = source.scrollLeft;
    }
  }

  syncTableScroll(event: Event): void {
    const source = event.target as HTMLElement;
    const tableSection = source.closest('.page-container')?.querySelector('.table-section') as HTMLElement | null;
    if (tableSection && Math.abs(tableSection.scrollLeft - source.scrollLeft) >= 1) {
      tableSection.scrollLeft = source.scrollLeft;
    }
  }

  resetTableScroll(): void {
    const container = document.querySelector('.page-container');
    const tableSection = container?.querySelector('.table-section') as HTMLElement | null;
    const scrollbar = container?.querySelector('.table-horizontal-scrollbar') as HTMLElement | null;
    if (tableSection) tableSection.scrollLeft = 0;
    if (scrollbar) scrollbar.scrollLeft = 0;
  }

  // 处理列宽调整事件
  onResize({ width }: NzResizeEvent, columnKey: string): void {
    this.columnWidths[columnKey] = `${width}px`;
    // 保存到本地存储
    localStorage.setItem('logTableColumnWidths', JSON.stringify(this.columnWidths));
  }

  // 在ngOnInit中加载保存的列宽
  ngOnInit(): void {
    this.loadData();

    // 从本地存储加载列宽设置
    const savedWidths = localStorage.getItem('logTableColumnWidths');
    if (savedWidths) {
      this.columnWidths = { ...this.columnWidths, ...JSON.parse(savedWidths) };
    }

    const savedSelectedColumns = localStorage.getItem('logSelectedColumnKeys');
    if (savedSelectedColumns) {
      try {
        const parsed = JSON.parse(savedSelectedColumns);
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.selectedColumnKeys = parsed;
        }
      } catch {}
    }
    this.columns = this.allColumns.filter(c => this.selectedColumnKeys.includes(c.key));

    const savedFilterKeys = localStorage.getItem('logFilterSelectedKeys');
    if (savedFilterKeys) {
      try {
        const parsed = JSON.parse(savedFilterKeys);
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.selectedFilterKeys = parsed;
        }
      } catch {}
    }

    // 从本地存储加载上次的筛选值（如果有）
    const savedFilterParams = localStorage.getItem('logFilterParams');
    if (savedFilterParams) {
      try {
        const parsed = JSON.parse(savedFilterParams);
        if (parsed && typeof parsed === 'object') {
          this.filterParams = { ...this.filterParams, ...parsed };
        }
      } catch {}
    }
  }

  loadData(): void {
    this.loading = true;
    this.http
      .post(ConstantUtil.ADMIN_LOG_LIST_V1_LIST, {
        pageIndex: this.pageIndex - 1,
        pageSize: this.pageSize,
        direction: this.direction,
        refreshTime: this.refreshTime,
        andFilterParams: this.transformFilterParams(this.filterParams, false),
        orFilterParams: this.transformFilterParams(this.filterParams, true)
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe((res: any) => {
        if (res.success) {
          this.listOfData = Array.isArray(res.data?.items) ? res.data.items : [];
          if (this.listOfData.length === 0) {
            this.resetTableScroll();
          }
          if (this.pageIndex == 1) {
            this.total = Number(res.data?.totalCount ?? res.data?.total ?? res.data?.count ?? this.listOfData.length);
          }
          this.cdr.detectChanges();
        }
      });
  }
  transformFilterParams(filterParams: any, orFilter: boolean): any[] {
    const result: any[] = [];
    for (const key in filterParams) {
      if (!filterParams.hasOwnProperty(key)) continue;

      const value = filterParams[key];
      if (!value) continue;

      if (orFilter) {
        this.handleOrFilter(key, value, result);
      } else {
        this.handleAndFilter(key, value, result);
      }
    }

    return result;
  }

  // 处理 OR 过滤逻辑
  private handleOrFilter(key: string, value: any, result: any[]): void {
    if (key !== 'keywords') return;

    const keywordFields = ['traceId', 'clientTraceId', 'requestText', 'accountId', 'requestPath'];
    keywordFields.forEach(field => {
      result.push({
        field,
        operator: 'contains',
        value
      });
    });
  }

  // 处理 AND 过滤逻辑
  private handleAndFilter(key: string, value: any, result: any[]): void {
    const filterHandlers = {
      requestTimeRange: () => this.handleTimeRangeFilter(value, result),
      hasError: () => this.handleErrorFilter(value, result),
      timeCost: () => this.handleTimeCostFilter(value, result),
      channel: () => this.handleChannelFilter(value, result),
      keywords: () => {} // keywords 在 AND 模式下不处理
    };

    const handler = filterHandlers[key as keyof typeof filterHandlers];
    if (handler) {
      handler();
    } else {
      // 默认处理其他字段
      this.handleDefaultFilter(key, value, result);
    }
  }

  // 处理渠道筛选为精确匹配
  private handleChannelFilter(value: any, result: any[]): void {
    result.push({
      field: 'channel',
      operator: 'eq',
      value
    });
  }

  // 处理时间范围过滤
  private handleTimeRangeFilter(value: any, result: any[]): void {
    if (!Array.isArray(value) || value.length !== 2) return;

    const [startTime, endTime] = value;

    if (startTime) {
      result.push({
        field: 'requestTime',
        operator: 'gte',
        value: this.isoStringToTimestamp(startTime)
      });
    }

    if (endTime) {
      result.push({
        field: 'requestTime',
        operator: 'lte',
        value: this.isoStringToTimestamp(endTime)
      });
    }
  }

  // 处理错误状态过滤
  private handleErrorFilter(value: any, result: any[]): void {
    result.push({
      field: 'hasError',
      operator: 'eq',
      value: value === 'true'
    });
  }

  // 处理时间消耗过滤
  private handleTimeCostFilter(value: any, result: any[]): void {
    result.push({
      field: 'timeCost',
      operator: 'gt',
      value
    });
  }

  // 处理默认字段过滤
  private handleDefaultFilter(key: string, value: any, result: any[]): void {
    result.push({
      field: key,
      operator: 'contains',
      value
    });
  }

  isoStringToTimestamp(isoString: string): number | null {
    if (!isoString) {
      return null;
    }

    const date = new Date(isoString);
    const timestamp = date.getTime();
    if (isNaN(timestamp)) {
      return null;
    }
    return timestamp;
  }

  nzPageIndexChange(index: number): void {
    this.pageIndex = index;
    this.loadData();
  }

  nzPageSizeChange(size: number): void {
    this.pageIndex = 1;
    this.pageSize = size;
    this.refreshTime = Date.now();
    this.loadData();
  }

  resetSearch() {
    this.filterParams = {
      keywords: '',
      name: '',
      traceId: '',
      clientTraceId: '',
      requestPath: '',
      channel: '',
      tokenId: '',
      osType: '',
      deviceName: '',
      requestTimeRange: [],
      hasError: '', // 重置状态筛选
      timeCost: '', // 重置耗时范围筛选
      uniqueId: '' // 重置设备ID筛选
    };
    // 同步重置后的筛选值到本地存储
    localStorage.setItem('logFilterParams', JSON.stringify(this.filterParams));
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.resetTableScroll();
    this.loadData();
  }
  search() {
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.resetTableScroll();
    this.loadData();
  }

  openFilterPopup(): void {
    this.nzModalService.create({
      nzTitle: this.i18n.fanyi('log.query-title'),
      nzContent: LogListFilterPopupComponent,
      nzData: {
        filterParams: this.filterParams,
        osTypeOptions: this.osTypeOptions,
        statusOptions: this.statusOptions,
        channelOptions: this.channelOptions,
        allFilterKeys: this.allFilterFields,
        selectedFilterKeys: this.selectedFilterKeys
      },
      nzOnOk: (instance?: any) => {
        if (instance) {
          if (instance.filterParams) {
            this.filterParams = { ...this.filterParams, ...instance.filterParams };
            localStorage.setItem('logFilterParams', JSON.stringify(this.filterParams));
          }
          if (instance.selectedFilterKeys) {
            this.selectedFilterKeys = [...instance.selectedFilterKeys];
            localStorage.setItem('logFilterSelectedKeys', JSON.stringify(this.selectedFilterKeys));
          }
        }
        this.cdr.detectChanges();
        this.pageIndex = 1;
        this.refreshTime = Date.now();
        this.loadData();
      },
      nzOnCancel: (instance?: any) => {
        // 取消时也保存弹窗内的选择，用于下次打开时回显，但不触发搜索
        if (instance) {
          if (instance.filterParams) {
            this.filterParams = { ...this.filterParams, ...instance.filterParams };
            localStorage.setItem('logFilterParams', JSON.stringify(this.filterParams));
          }
          if (instance.selectedFilterKeys) {
            this.selectedFilterKeys = [...instance.selectedFilterKeys];
            localStorage.setItem('logFilterSelectedKeys', JSON.stringify(this.selectedFilterKeys));
          }
        }
        this.cdr.detectChanges();
      },
      nzWidth: '700px'
    });
  }

  hasActiveFilters(): boolean {
    if (this.filterParams.keywords) return true;

    for (const key in this.filterParams) {
      if (key !== 'keywords' && key !== 'requestTimeRange') {
        if (this.filterParams[key]) return true;
      }
    }

    if (
      this.filterParams.requestTimeRange &&
      Array.isArray(this.filterParams.requestTimeRange) &&
      this.filterParams.requestTimeRange.length === 2
    ) {
      if (this.filterParams.requestTimeRange[0] || this.filterParams.requestTimeRange[1]) {
        return true;
      }
    }

    return false;
  }

  // 获取活跃的筛选条件用于展示
  getActiveFilters(): Array<{ key: string; label: string; value: string }> {
    const activeFilters: Array<{ key: string; label: string; value: string }> = [];

    // 处理关键词
    this.addKeywordsFilter(activeFilters);

    // 处理普通文本字段
    this.addTextFilters(activeFilters);

    // 处理时间范围
    this.addTimeRangeFilter(activeFilters);

    // 处理错误状态
    this.addErrorStatusFilter(activeFilters);

    // 处理耗时范围
    this.addTimeCostFilter(activeFilters);

    return activeFilters;
  }

  // 添加关键词过滤条件
  private addKeywordsFilter(activeFilters: Array<{ key: string; label: string; value: string }>): void {
    if (!this.filterParams.keywords) return;

    activeFilters.push({
      key: 'keywords',
      label: this.i18n.fanyi('log.keywords'),
      value: this.filterParams.keywords
    });
  }

  // 添加普通文本字段过滤条件
  private addTextFilters(activeFilters: Array<{ key: string; label: string; value: string }>): void {
    const excludedKeys = ['keywords', 'requestTimeRange', 'hasError', 'timeCost'];

    for (const key in this.filterParams) {
      if (this.shouldSkipTextFilter(key, excludedKeys)) continue;

      const filterParam = this.filterParamsKey.find(param => param.key === key);
      activeFilters.push({
        key,
        label: filterParam ? filterParam.label : key,
        value: this.filterParams[key]
      });
    }
  }

  // 判断是否应该跳过文本过滤条件
  private shouldSkipTextFilter(key: string, excludedKeys: string[]): boolean {
    return excludedKeys.includes(key) || !this.filterParams[key];
  }

  // 添加时间范围过滤条件
  private addTimeRangeFilter(activeFilters: Array<{ key: string; label: string; value: string }>): void {
    const timeRange = this.filterParams.requestTimeRange;
    if (!this.isValidTimeRange(timeRange)) return;

    const [startTime, endTime] = timeRange;
    const timeFilter = this.createTimeRangeFilter(startTime, endTime);

    if (timeFilter) {
      activeFilters.push(timeFilter);
    }
  }

  // 验证时间范围是否有效
  private isValidTimeRange(timeRange: any): boolean {
    return timeRange && Array.isArray(timeRange) && timeRange.length === 2;
  }

  // 创建时间范围过滤条件（优化版）
  private createTimeRangeFilter(startTime: any, endTime: any): { key: string; label: string; value: string } | null {
    const timeFilters = {
      both: () => ({
        key: 'requestTimeRange',
        label: this.i18n.fanyi('log.request-time-range'),
        value: `${new Date(startTime).toLocaleDateString()} - ${new Date(endTime).toLocaleDateString()}`
      }),
      start: () => ({
        key: 'requestTimeRange',
        label: this.i18n.fanyi('log.request-time-start'),
        value: new Date(startTime).toLocaleDateString()
      }),
      end: () => ({
        key: 'requestTimeRange',
        label: this.i18n.fanyi('log.request-time-end'),
        value: new Date(endTime).toLocaleDateString()
      })
    };

    if (startTime && endTime) return timeFilters.both();
    if (startTime) return timeFilters.start();
    if (endTime) return timeFilters.end();

    return null;
  }

  // 添加错误状态过滤条件
  private addErrorStatusFilter(activeFilters: Array<{ key: string; label: string; value: string }>): void {
    if (!this.filterParams.hasError) return;

    activeFilters.push({
      key: 'hasError',
      label: this.i18n.fanyi('log.status-label'),
      value: this.filterParams.hasError === 'true' ? this.i18n.fanyi('log.failed-status') : this.i18n.fanyi('log.success-status')
    });
  }

  // 添加耗时过滤条件
  private addTimeCostFilter(activeFilters: Array<{ key: string; label: string; value: string }>): void {
    if (!this.filterParams.timeCost) return;

    activeFilters.push({
      key: 'timeCost',
      label: this.i18n.fanyi('log.time-cost-greater-than'),
      value: `${this.filterParams.timeCost}ms`
    });
  }

  // 清除单个筛选条件
  clearFilter(key: string): void {
    if (key === 'requestTimeRange') {
      this.filterParams.requestTimeRange = [];
    } else {
      this.filterParams[key] = '';
    }
    this.search();
  }

  // 处理列选择变更
  onColumnChecked(columnKey: string, checked: boolean): void {
    const set = new Set(this.selectedColumnKeys);
    if (checked) set.add(columnKey);
    else set.delete(columnKey);
    this.selectedColumnKeys = Array.from(set);
    this.columns = this.allColumns.filter(c => this.selectedColumnKeys.includes(c.key));
    localStorage.setItem('logSelectedColumnKeys', JSON.stringify(this.selectedColumnKeys));
    this.cdr.detectChanges();
  }

  // 显示 Trace 详情
  showTraceDetails(data: any): void {
    // 实现查看 Trace 详情的功能，可以是弹窗或跳转到详情页
    console.log('查看 Trace 详情:', data);
    this.nzModalService.info({
      nzTitle: this.i18n.fanyi('log.trace-details'),
      nzContent: `<pre>${JSON.stringify(data, null, 2)}</pre>`,
      nzWidth: '800px'
    });
  }
  openLogDetail(data: any): void {
    // 使用Location服务生成完整的URL
    const currentUrl = this.location.prepareExternalUrl(`/logs/detail/${data.id}`);

    // 确保URL包含适当的哈希前缀
    const fullUrl = window.location.origin + currentUrl;

    // 在新标签页中打开
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  }
}
